import React, { useEffect, useState } from 'react';
import { SurveysAPI } from '../../API';
import { getVerisoulSessionId } from '../../Utils/verisoulHelper';
import { callbackUserId, decideCallbackOutcome, displayFor, endLinkQuery, INVALID_LINK_MESSAGE, linkUserId } from './callbackOutcome';

// Brand-neutral DX (DataXing) callback landing page.
//
// This page is the FIRST hop after a respondent finishes a survey. The survey
// provider redirects here; we record the hit via
// /api/v1/surveys/GetUserOneAssignedSurveyCallback and then forward onward:
//
//   - Vendor flow:    response.url is the partner passback URL → forward
//   - Panelist flow:  response.url is the country panel end page URL → forward
//   - Test flow:      response.url is empty → render the Test Recorded card
//
// We deliberately do NOT render the panelist UI (UID, points, disclaimer,
// country branding) here. The country panel page is the sole renderer of that
// view, after we forward the panelist there. Rendering it on this hop too
// would leak panel branding into the brand-neutral DX surface.
//
// The endpoint is unauthenticated; this page is also outside the admin auth
// wrapper (App.tsx mounts these routes BEFORE PrivateRouteWithRedux).

type Status = 'completed' | 'terminate' | 'overquota' | 'qualityterminate';

const STATUS_MAP: Record<Status, { title: string; backendStatus: string }> = {
    completed:        { title: 'Completed',          backendStatus: 'Completed' },
    terminate:        { title: 'Terminated',         backendStatus: 'Terminated' },
    overquota:        { title: 'Over Quota',         backendStatus: 'Over Quota' },
    qualityterminate: { title: 'Quality Terminated', backendStatus: 'Quality Terminated' },
};

// Per-status accent color used in the Status cell (Step P7). Over Quota uses
// orange with dark text for contrast against the light-yellow fill.
const STATUS_THEME: Record<Status, { bg: string; fg: string }> = {
    completed:        { bg: '#28a745', fg: '#ffffff' },
    terminate:        { bg: '#dc3545', fg: '#ffffff' },
    overquota:        { bg: '#ffc107', fg: '#212529' },
    qualityterminate: { bg: '#6c757d', fg: '#ffffff' },
};

interface Props {
    status: Status;
}

const SurveyCallbackPage: React.FC<Props> = ({ status }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [recorded, setRecorded] = useState<{ surveyId: string; userId: string; status: string } | null>(null);

    const statusInfo = STATUS_MAP[status] || STATUS_MAP.completed;
    // What is shown is the status the backend reported (the STORED one on a
    // repeat hit), never the one in the path. Nothing is shown until it answers.
    const statusTheme = (recorded && displayFor(recorded.status)) || STATUS_THEME[status] || STATUS_THEME.completed;
    const shownTitle = (recorded && displayFor(recorded.status)?.title) || statusInfo.title;

      useEffect(() => {
        const processCallback = async () => {
            // HashRouter puts query string after the hash: #/surveys/completed?surveyid=X&userid=Y
            //
            // Read it out of the hash specifically. Splitting the whole href on '?'
            // returns whatever sits BEFORE the '#' when a survey provider appends
            // its own params to the callback URL, which is a different query string
            // entirely — surveyid comes back null and userid comes back with the
            // route path glued to it. normalizeTrackingParams() (index.tsx, before
            // the router boots) has already folded any such pre-hash params into the
            // hash query by the time this runs, so everything is here.
            const hash = window.location.hash || '';
            const queryStart = hash.indexOf('?');
            const queryString = queryStart === -1 ? '' : hash.slice(queryStart + 1);
            const params = new URLSearchParams(queryString);
            const surveyId = params.get('surveyid');
            // A missing or placeholder userid ("{userid}" the client never
            // substituted) is still POSTed, as '' with placeholderUserId=true:
            // the backend matches the entry from this IP within this survey.
            // Recontact links: a UUID newids is our id and wins over userid.
            const userIdPart = callbackUserId(linkUserId(params));
            const userId = userIdPart.userId;
            // Pre-Fix-4 audit confirmed panelist URLs never carry partnerid; vendor
            // URLs do. Reading from URL preserves that distinction so the backend
            // can route vendors through the partner branch.
            const partnerId = params.get('partnerid') || 'NA';
            // `source=prescreen` is appended by preScreen.controller.js on the
            // prescreener-fail redirect to this page. This page is the FIRST hop
            // for all three flows (test / panelist / partner) — the country
            // thank-you page only loads AFTER we forward, by which time the row
            // is already written. So the PQ flag MUST be forwarded from here,
            // not from the country page, or every prescreener-fail hit lands in
            // Quality Term. instead of PQ Terminated. Only forward when true:
            // the backend conditionally writes on typeof === 'boolean', and a
            // spurious `false` on a re-fire would overwrite a real prior `true`.
            const isPreScreenTerminated = params.get('source') === 'prescreen';

            if (!surveyId) {
                console.warn('Callback page: missing surveyid');
                setError(INVALID_LINK_MESSAGE);
                setLoading(false);
                return;
            }

            let verisoulSessionId = null;
            const isVerisoulEnabled = await SurveysAPI.checkVerisoulEnabled(surveyId);
            if (isVerisoulEnabled) {
                try {
                    verisoulSessionId = await getVerisoulSessionId();
                } catch (e) {
                    console.error("Verisoul SDK error:", e);
                }
            }

            SurveysAPI.GetUserOneAssignedSurveyCallback({
                surveyId,
                ...userIdPart,
                partnerId,
                status: statusInfo.backendStatus,
                verisoulSessionId,
                endLinkQuery: endLinkQuery(params),
                ...(isPreScreenTerminated ? { isPreScreenTerminated: true } : {}),
            })
                .then((result: any) => {
                    // The page renders only from the backend's answer (see
                    // callbackOutcome.ts). Reasons stay in the console.
                    const decision = decideCallbackOutcome(result, statusInfo.backendStatus);
                    if (decision.kind === 'redirect') {
                        // replace(): Back must not return here and re-post the hit.
                        window.location.replace(decision.url);
                        return;
                    }
                    if (decision.kind === 'invalid') {
                        console.warn('Callback not recorded:', decision.reason);
                        setError(INVALID_LINK_MESSAGE);
                        setLoading(false);
                        return;
                    }
                    // Recorded with no onward URL: the admin PM Test flow
                    // (Test Recorded card) or a repeat hit with no URL.
                    setRecorded({ surveyId, userId, status: decision.status });
                    setLoading(false);
                })
                .catch((err: any) => {
                    console.error('Callback request failed:', err);
                    setError(INVALID_LINK_MESSAGE);
                    setLoading(false);
                });
        };
        processCallback();
    }, [status, statusInfo.backendStatus]);

    // Shared logo header used by all three states (loading / error / success).
    const logo = (
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <img
                src="/DX Logo.avif"
                alt="DataXing"
                style={{ width: '140px', height: 'auto' }}
            />
        </div>
    );

    return (
        <div
            style={{
                minHeight: '100vh',
                background: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px',
            }}
        >
            <div style={{ width: '100%', maxWidth: '800px' }}>
                {logo}

                {loading && (
                    <div style={{ textAlign: 'center', color: '#555', fontSize: '1rem' }}>
                        Recording…
                    </div>
                )}

                {!loading && error && (
                    <div
                        style={{
                            textAlign: 'center',
                            color: '#212529',
                            fontSize: '1rem',
                            border: '1px solid #dee2e6',
                            background: '#fafafa',
                            padding: '16px 20px',
                            borderRadius: '4px',
                        }}
                    >
                        {error}
                    </div>
                )}

                {!loading && !error && recorded && (
                    <>
                        <table
                            style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontFamily: 'Arial, sans-serif',
                                fontSize: '0.95rem',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                            }}
                        >
                            <thead>
                                <tr style={{ background: '#212529', color: '#ffffff' }}>
                                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, border: '1px solid #212529' }}>
                                        Project ID
                                    </th>
                                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, border: '1px solid #212529' }}>
                                        Respondent ID
                                    </th>
                                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, border: '1px solid #212529' }}>
                                        Status
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr style={{ background: '#fafafa' }}>
                                    <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #dee2e6', wordBreak: 'break-all' }}>
                                        {recorded.surveyId}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #dee2e6', wordBreak: 'break-all' }}>
                                        {recorded.userId || '—'}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                                        <span
                                            style={{
                                                display: 'inline-block',
                                                padding: '4px 12px',
                                                borderRadius: '12px',
                                                background: statusTheme.bg,
                                                color: statusTheme.fg,
                                                fontWeight: 600,
                                                fontSize: '0.85rem',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {shownTitle}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <div
                            style={{
                                textAlign: 'center',
                                marginTop: '24px',
                                color: '#555',
                                fontSize: '0.9rem',
                            }}
                        >
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SurveyCallbackPage;
